import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent } from '@blueprintjs/core';

import { Button } from 'components/buttons';
import { FormNumericInput, FormSelect, FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Modal, ModalSize } from 'components/notifications';

// The sign-off labels as the Employee microservice numbers them.
export const signOffLabelIds = {
    knownDifference: 1,
    regression: 2
};

export const maxReasonLength = 1000;
// Work items are stored as a 32-bit integer.
const maxWorkItemId = 2147483647;

const labelItems = [
    { id: signOffLabelIds.knownDifference, name: "Known difference" },
    { id: signOffLabelIds.regression, name: "Regression" }
];

const blankReasonMessage = "Enter a reason, not only spaces.";
const longReasonMessage = `The reason can be at most ${maxReasonLength} characters.`;
const invalidWorkItemMessage = "Enter the work item as a whole number above 0.";
const countingText = "Counting the open differences in this group…";
const countFailedText = "The open differences in this group could not be counted, so nothing can be signed off. Close this and try again.";
const noOpenText = "There are no open differences in this group to sign off.";

function formatDifferences(count) {
    return `${count} ${count === 1 ? "difference" : "differences"}`;
}

function isWorkItemValid(workItem) {
    return Number.isInteger(workItem) && workItem > 0;
}

function buildInitialForm(initialJudgement) {
    return {
        labelId: initialJudgement?.labelId ?? null,
        reason: initialJudgement?.reason ?? "",
        workItem: initialJudgement?.workItemId ?? ""
    };
}

function buildGroupSummary(openCount, countLoading, countFailed) {
    if (countFailed) {
        return countFailedText;
    }

    if (countLoading || openCount == null) {
        return countingText;
    }

    if (openCount === 0) {
        return noOpenText;
    }

    return `${formatDifferences(openCount)} still open in this group will be signed off with this judgement.`;
}

/**
 * The judgement recorded for one difference or, when a group is being signed off, for every open
 * difference in it. The checks mirror the endpoint's, so a judgement it would reject is never posted.
 */
export function DiffViewerSignOffModal(
    {
        isOpen = false,
        title = "",
        initialJudgement = null,
        group = false,
        openCount = null,
        countLoading = false,
        countFailed = false,
        submitting = false,
        onSubmit,
        onClose
    }
) {
    const [form, setForm] = useState(buildInitialForm(initialJudgement));

    useEffect(() => {
        if (isOpen) {
            setForm(buildInitialForm(initialJudgement));
        }
    }, [isOpen]);

    const isRegression = form.labelId === signOffLabelIds.regression;
    const reasonBlank = form.reason.trim() === "";
    const reasonTooLong = form.reason.length > maxReasonLength;
    const workItemValid = isWorkItemValid(form.workItem);
    let reasonError = null;
    let workItemError = null;

    if (form.reason !== "" && reasonBlank) {
        reasonError = blankReasonMessage;
    } else if (reasonTooLong) {
        reasonError = longReasonMessage;
    }

    if (isRegression && form.workItem !== "" && !workItemValid) {
        workItemError = invalidWorkItemMessage;
    }

    const formValid = form.labelId != null && !reasonBlank && !reasonTooLong && (!isRegression || workItemValid);
    const groupReady = !countLoading && !countFailed && openCount != null && openCount > 0;
    const canSubmit = formValid && !submitting && (!group || groupReady);

    let submitText = "Sign off";

    if (group && groupReady) {
        submitText = `Sign off ${formatDifferences(openCount)}`;
    }

    // A known difference never carries a work item, so leaving Regression drops the one entered.
    function onLabelSelect(item) {
        setForm((current) => ({
            ...current,
            labelId: item.id,
            workItem: item.id === signOffLabelIds.regression ? current.workItem : ""
        }));
    }

    function onReasonChange(event) {
        const reason = event.target.value;

        setForm((current) => ({ ...current, reason: reason }));
    }

    function onWorkItemChange(value) {
        setForm((current) => ({ ...current, workItem: value }));
    }

    function onSubmitClick() {
        if (!canSubmit) {
            return;
        }

        onSubmit({
            labelId: form.labelId,
            reason: form.reason.trim(),
            workItemId: isRegression ? form.workItem : null
        });
    }

    function onCloseClick() {
        if (!submitting) {
            onClose();
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCloseClick}
            title={title}
            size={ModalSize.SMALL}
            clickOutsideClose={!submitting}
            showCloseButton={false}
            footer={(
                <div className="inline-items push-right">
                    <Button
                        id="rules-engine-diff-sign-off-cancel"
                        text="Cancel"
                        intent={Intent.NONE}
                        disabled={submitting}
                        onClick={onCloseClick}
                    />
                    <Button
                        id="rules-engine-diff-sign-off-submit"
                        text={submitText}
                        intent={Intent.PRIMARY}
                        loading={submitting}
                        disabled={!canSubmit}
                        onClick={onSubmitClick}
                    />
                </div>
            )}
        >
            <ShowHide
                evaluator={group}
                show={(
                    <p id="rules-engine-diff-sign-off-count" className={classNames({ "text-intent-danger": countFailed })}>
                        {buildGroupSummary(openCount, countLoading, countFailed)}
                    </p>
                )}
            />
            <FormSelect
                id="rules-engine-diff-sign-off-label"
                headingText="Label"
                placeholder="Choose a label"
                items={labelItems}
                selectedValue={form.labelId}
                disabled={submitting}
                onItemSelect={onLabelSelect}
            />
            <FormTextArea
                id="rules-engine-diff-sign-off-reason"
                headingText="Reason"
                value={form.reason}
                maxLength={maxReasonLength}
                showCounter
                fill
                disabled={submitting}
                dangerHelperText={reasonError}
                onChange={onReasonChange}
            />
            <ShowHide
                evaluator={isRegression}
                show={(
                    <FormNumericInput
                        id="rules-engine-diff-sign-off-work-item"
                        headingText="Work item"
                        selectedValue={form.workItem}
                        min={1}
                        max={maxWorkItemId}
                        disabled={submitting}
                        dangerHelperText={workItemError}
                        onValueChange={onWorkItemChange}
                    />
                )}
            />
        </Modal>
    );
}

DiffViewerSignOffModal.propTypes = {
    isOpen: PropTypes.bool,
    title: PropTypes.string,
    initialJudgement: PropTypes.object,
    group: PropTypes.bool,
    openCount: PropTypes.number,
    countLoading: PropTypes.bool,
    countFailed: PropTypes.bool,
    submitting: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
